import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Workspace {
  id: string;
  client_name: string;
  company_name: string | null;
  is_active: boolean;
  enable_viral_content?: boolean;
  viral_variations_count?: number;
  viral_min_score?: number;
  viral_preferred_types?: string[];
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  isAgency: boolean;
  userRole: 'admin' | 'agency' | null;
  currentWorkspace: Workspace | null;
  setCurrentWorkspace: (workspace: Workspace | null) => void;
  workspaces: Workspace[];
  agencyWallet: { id: string; balance: number } | null;
  loadAgencyWallet: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  isAdmin: false,
  isAgency: false,
  userRole: null,
  currentWorkspace: null,
  setCurrentWorkspace: () => {},
  workspaces: [],
  agencyWallet: null,
  loadAgencyWallet: async () => {},
  isLoading: true,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAgency, setIsAgency] = useState(false);
  // MIGRATION: Removed 'user' role - only 'admin' and 'agency' roles exist now
  const [userRole, setUserRole] = useState<'admin' | 'agency' | null>(null);
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [agencyWallet, setAgencyWallet] = useState<{ id: string; balance: number } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let refreshInterval: NodeJS.Timeout;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;
        
        console.log('[AuthContext] Auth state changed:', event);
        setSession(session);
        setUser(session?.user ?? null);
        
        // Defer role check with setTimeout to avoid deadlock
        if (session?.user) {
          setTimeout(() => {
            if (mounted) {
              checkUserRole(session.user.id);
            }
          }, 0);
        } else {
          setIsAdmin(false);
          setIsAgency(false);
          setUserRole(null);
          setCurrentWorkspace(null);
          setWorkspaces([]);
        }
      }
    );

    // THEN check for existing session
    // CRITICAL FIX #48: Refresh session before checking
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!mounted) return;
      
      // If session exists but might be stale, try refreshing
      if (session) {
        const { data: { session: refreshedSession } } = await supabase.auth.refreshSession();
        if (refreshedSession) {
          session = refreshedSession;
          console.log('[AuthContext] Session refreshed on mount');
        }
      }
      
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        checkUserRole(session.user.id);
      } else {
        setIsLoading(false);
      }
    });

    // BUG FIX #4: Periodic session refresh every 50 minutes (before 60-minute expiry)
    refreshInterval = setInterval(async () => {
      if (!mounted) return;
      
      console.log('[AuthContext] Attempting periodic session refresh...');
      const { data: { session }, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error('[AuthContext] Session refresh failed:', error);
        // Session expired - redirect to login
        toast.error('Session Expired', {
          description: 'Please log in again to continue',
          duration: 5000
        });
      } else if (session) {
        console.log('[AuthContext] Session refreshed successfully');
        setSession(session);
        setUser(session.user);
      }
    }, 50 * 60 * 1000); // 50 minutes

    return () => {
      mounted = false;
      subscription.unsubscribe();
      if (refreshInterval) clearInterval(refreshInterval);
    };
  }, []);

  const checkUserRole = async (userId: string) => {
    try {
      console.log('[AuthContext] Checking roles for user:', userId);
      
      // Check for admin role
      const { data: isAdminData, error: adminError } = await supabase.rpc('has_role', {
        _user_id: userId,
        _role: 'admin'
      });

      if (adminError) {
        console.error('[AuthContext] Admin role check error:', adminError);
      }

      // Check for agency role (using any to bypass type check until types regenerate)
      const { data: isAgencyData, error: agencyError } = await (supabase.rpc as any)('is_agency', {
        _user_id: userId
      });

      if (agencyError) {
        console.error('[AuthContext] Agency role check error:', agencyError);
      }

      const hasAdminRole = !adminError && isAdminData === true;
      const hasAgencyRole = !agencyError && isAgencyData === true;

      console.log('[AuthContext] Role check results:', {
        hasAdminRole,
        hasAgencyRole,
        adminData: isAdminData,
        agencyData: isAgencyData
      });

      // Set BOTH flags if user has both roles
      setIsAdmin(hasAdminRole);
      setIsAgency(hasAgencyRole);
      
      // MIGRATION: Set primary role (admin takes precedence)
      // All users are now either admin or agency (no regular 'user' role)
      if (hasAdminRole) {
        setUserRole('admin');
      } else if (hasAgencyRole) {
        setUserRole('agency');
      } else {
        // MIGRATION: If user has no role, something is wrong - they should have agency role
        console.error('[AuthContext] User has no role assigned - this should not happen after migration');
        setUserRole('agency'); // Default to agency for safety
      }

      // Fetch workspaces and wallet for ALL agency users (regardless of admin status)
      if (hasAgencyRole) {
        console.log('[AuthContext] Loading agency data...');
        await fetchWorkspaces(userId, true);
        await loadAgencyWalletData(userId);
      }

    } catch (error) {
      console.error('[AuthContext] Error checking user role:', error);
      setIsAdmin(false);
      setIsAgency(false);
      setUserRole('agency'); // MIGRATION: Default to agency instead of 'user'
    } finally {
      console.log('[AuthContext] Role check complete, setting isLoading to false');
      setIsLoading(false);
    }
  };

  const fetchWorkspaces = async (userId: string, isAgencyUser: boolean) => {
    try {
      // Get user profile to find current workspace
      const profileQuery = supabase
        .from('user_profiles')
        .select('current_workspace_id')
        .eq('id', userId)
        .maybeSingle();
      
      const { data: profile } = await (profileQuery as any);

      if (isAgencyUser) {
        // Fetch all workspaces owned by the agency (using any to bypass type check)
        const workspacesQuery = (supabase as any)
          .from('workspaces')
          .select('id, client_name, company_name, is_active')
          .eq('agency_id', userId)
          .eq('is_active', true)
          .order('created_at', { ascending: false });

        const { data: agencyWorkspaces, error } = await workspacesQuery;

        if (!error && agencyWorkspaces && agencyWorkspaces.length > 0) {
          setWorkspaces(agencyWorkspaces as Workspace[]);
          
          // Set current workspace to saved workspace or first workspace
          let selectedWorkspace: Workspace | null = null;
          
          if (profile?.current_workspace_id) {
            const currentWs = (agencyWorkspaces as any[]).find((w: any) => w.id === profile.current_workspace_id);
            selectedWorkspace = (currentWs || agencyWorkspaces[0]) as Workspace;
          } else {
            // Auto-set first workspace if none is selected
            selectedWorkspace = agencyWorkspaces[0] as Workspace;
            
            // Update database with first workspace
            await supabase
              .from('user_profiles')
              .update({ current_workspace_id: selectedWorkspace.id })
              .eq('id', userId);
          }
          
          setCurrentWorkspace(selectedWorkspace);
        }
      } else {
        // Regular user - fetch their personal workspace (using any to bypass type check)
        if (profile?.current_workspace_id) {
          const workspaceQuery = (supabase as any)
            .from('workspaces')
            .select('id, client_name, company_name, is_active')
            .eq('id', profile.current_workspace_id)
            .maybeSingle();

          const { data: workspace } = await workspaceQuery;

          if (workspace) {
            setCurrentWorkspace(workspace as Workspace);
            setWorkspaces([workspace] as Workspace[]);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching workspaces:', error);
    }
  };

  const loadAgencyWalletData = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('agency_wallets')
        .select('id, balance')
        .eq('agency_id', userId)
        .maybeSingle();
      
      if (!error && data) {
        setAgencyWallet(data);
      }
    } catch (error) {
      console.error('Error loading agency wallet:', error);
    }
  };

  const loadAgencyWallet = async () => {
    if (user && isAgency) {
      await loadAgencyWalletData(user.id);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      isAdmin, 
      isAgency, 
      userRole,
      currentWorkspace,
      setCurrentWorkspace,
      workspaces,
      agencyWallet,
      loadAgencyWallet,
      isLoading 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
